// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { AFFILIATE_PARAM, URL_PARAMS } from '@seedplanner/shared-utils';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Image } from '../components/common/image';
import { Box, Button, Typography } from '../lib/base';
import { Link } from '../lib/link';
import * as routes from '../routes';

const Section = ({ children, backgroundColor }: { children: React.ReactNode; backgroundColor?: string }) => {
  return (
    <Box
      sx={{
        background: backgroundColor ?? undefined,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '600px', md: '1200px' },
          margin: '0 auto',
          paddingTop: { xs: 4, md: 5 },
          paddingBottom: { xs: 4, md: 5 },
          paddingLeft: 3,
          paddingRight: 3,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const ResponsiveGrid = ({
  children,
  image,
  reverse,
}: { children: React.ReactNode; image?: React.ReactNode; reverse?: boolean }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: {
        xs: 'column',
        md: reverse ? 'row-reverse' : 'row',
      },
      alignItems: 'center',
      gap: { xs: 4, md: 6 },
    }}
  >
    <Box
      sx={{
        width: { xs: '100%', md: '50%' },
      }}
    >
      {children}
    </Box>
    {image && (
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
        }}
      >
        {image}
      </Box>
    )}
  </Box>
);

const Title = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ mb: 3 }} variant="h4">
    {children}
  </Typography>
);

const StyledList = styled('ul')({
  textAlign: 'left',
});

const HeroImage = () => (
  <Image
    sx={{
      height: 'auto',
      width: '100%',
      position: 'relative',
      borderRadius: '4px',
      transform: 'perspective(2500px) rotateY(-15deg) rotateX(4deg) translateX(-5%)',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'perspective(1500px) rotateY(0deg) rotateX(0deg) translateX(-5%)',
      },
    }}
    eager
    width={710}
    height={400}
    altText="Microgreen Manager screenshot"
    imageUrl={routes.makePublic('/screenshot-710.jpeg')}
  />
);

const HeroSection = ({ signupRoute }: { signupRoute: string }) => {
  const navigate = useNavigate();
  const tm = useTheme();

  const gradientBackground = `linear-gradient(180deg, ${tm.palette.primary.main}, ${tm.palette.primary.dark})`;

  return (
    <Box sx={{ background: gradientBackground }}>
      <Box
        sx={{
          paddingTop: { xs: 4, md: 5 },
          paddingBottom: { xs: 4, md: 5 },
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ display: { xs: 'block', md: 'flex' }, flex: '1 1 50%' }}>
          <Box
            sx={{
              color: 'primary.contrastText',
              textAlign: { xs: 'center', md: 'left' },
              display: { xs: 'flex', md: 'block' },
              flexDirection: 'column',
              alignItems: 'center',
              marginLeft: 'auto',
              marginRight: { xs: 'auto', md: 0 },
              maxWidth: '576px', // Calculated to line up with other panels on left side
              paddingLeft: 3,
              paddingRight: { xs: 3, md: 0 },
              paddingTop: 0,
              paddingBottom: 0,
            }}
          >
            <Typography sx={{ mb: 2 }} variant="h3">
              Take the Guesswork Out of Growing Microgreens
            </Typography>
            <Typography sx={{ mb: 5 }} variant="body1">
              Microgreen Manager automatically creates recurring orders, calculates trays, and creates tasks for all
              stages of production, so you never miss anything.
            </Typography>
            <Button
              size="large"
              variant="contained"
              color="secondary"
              onClick={() => {
                navigate(signupRoute);
              }}
              sx={{ maxWidth: '300px', textTransform: 'none' }}
            >
              <Typography variant="body1">Sign Up For The Free Beta</Typography>
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flex: '1 1 50%',
            paddingBottom: 4,
          }}
        >
          <HeroImage />
        </Box>
      </Box>
    </Box>
  );
};

const TaskScreenshot = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Image
      sx={{
        height: 'auto',
        width: '100%',
        maxWidth: '300px',
        position: 'relative',
        borderRadius: '4px',
        border: '1px solid grey',
      }}
      eager
      width={300}
      height={169}
      altText="Microgreen Manager screenshot"
      imageUrl={routes.makePublic('/mobile-450.jpeg')}
    />
  </Box>
);
const FeaturesSection = () => {
  return (
    <Section>
      <ResponsiveGrid reverse image={<TaskScreenshot />}>
        <Box
          sx={{
            textAlign: { xs: 'center', md: 'left' },
            display: { xs: 'flex', md: 'block' },
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Title>How Does Microgreen Manager Work?</Title>
          <StyledList>
            <li>
              <Typography sx={{ mb: 2 }} variant="body1">
                Step 1: Enter your crops, blends, and yields.
              </Typography>
            </li>
            <li>
              <Typography sx={{ mb: 2 }} variant="body1">
                Step 2: Create your single orders and recurring orders.
              </Typography>
            </li>
            <li>
              <Typography sx={{ mb: 2 }} variant="body1">
                Step 3: Follow the production tasks automatically created by Microgreen Manager.
              </Typography>
            </li>
            <li>
              <Typography sx={{ mb: 2 }} variant="body1">
                Step 4: Mark tasks as completed as you grow.
              </Typography>
            </li>
            <li>
              <Typography sx={{ mb: 2 }} variant="body1">
                Step 5: Harvest and package your crops and blends with the harvest summaries and pack lists created by
                Microgreen Manager.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">Step 6: Profit!</Typography>
            </li>
          </StyledList>
        </Box>
      </ResponsiveGrid>
    </Section>
  );
};

const TeamImage = () => (
  <Image
    sx={{
      height: 'auto',
      width: '100%',
      position: 'relative',
      borderRadius: '4px',
    }}
    width={450}
    height={253}
    altText="Microgreen Manager team photo"
    imageUrl={routes.makePublic('/team-450.jpeg')}
  />
);
const TeamSection = () => {
  const tm = useTheme();
  const gradientBackground = `linear-gradient(180deg, ${tm.palette.primary.dark}, ${tm.palette.primary.main})`;
  return (
    <Section backgroundColor={gradientBackground}>
      <ResponsiveGrid image={<TeamImage />}>
        <Box
          sx={{
            color: 'primary.contrastText',
            transition: 'margin 0.4s',
            textAlign: { xs: 'center', md: 'left' },
            display: { xs: 'flex', md: 'block' },
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Title>Meet the Team</Title>
          <Typography sx={{ mb: 2 }} variant="body1">
            We're a small and passionate team.
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body1">
            Garrett Corwin (right) is a seasoned commercial microgreen farmer. He has been farming for over 6 years, and
            growing microgreens commercially for over 4 years. His farm, Piedmont Microgreens, grows over 300 trays per
            week, with a full-time team.
          </Typography>
          <Typography variant="body1">
            Spencer Corwin (left) is a veteran software engineer, with over 5 years of experience, including recently at
            Meta Platforms (also known as Facebook).
          </Typography>
        </Box>
      </ResponsiveGrid>
    </Section>
  );
};

const CustomerSupportImage = () => (
  <Image
    sx={{
      height: 'auto',
      width: '100%',
      position: 'relative',
      borderRadius: '4px',
    }}
    width={450}
    height={253}
    altText="Microgreen Manager screenshot"
    imageUrl={routes.makePublic('/ipad-450.jpeg')}
  />
);
const CustomerSupportSection = () => {
  return (
    <Section>
      <ResponsiveGrid reverse image={<CustomerSupportImage />}>
        <Box
          sx={{
            transition: 'margin 0.4s',
            textAlign: { xs: 'center', md: 'left' },
            display: { xs: 'flex', md: 'block' },
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Title>Unbeatable Customer Support</Title>
          <Typography sx={{ mb: 2 }} variant="body1">
            Do you have a question? Have a feature request? Need help using the software? We're here for you and there
            are many ways to reach us.
          </Typography>
          <StyledList>
            <li>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Message us on{' '}
                <Link
                  newTab
                  inlineText
                  variant="body1"
                  path="https://facebook.com/microgreenmanager"
                  title="Facebook"
                />
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Message us on{' '}
                <Link
                  newTab
                  inlineText
                  variant="body1"
                  path="https://instagram.com/microgreenmanager"
                  title="Instagram"
                />
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Send us a message through our{' '}
                <Link inlineText variant="body1" path={routes.CONTACT} title="contact form" />
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Submit a request through our{' '}
                <Link newTab inlineText variant="body1" path={routes.FEEDBACK} title="feedback portal" />
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Or email us at{' '}
                <Link
                  inlineText
                  variant="body1"
                  path={'mailto:support@microgreenmanager.com'}
                  title="support@microgreenmanager.com"
                />
              </Typography>
            </li>
          </StyledList>
          <Typography variant="body1">
            You can message us at any time. We usually respond within minutes or hours of receiving messages.
          </Typography>
        </Box>
      </ResponsiveGrid>
    </Section>
  );
};

export default function Home() {
  const [searchParams] = useSearchParams();
  const [aff, setAff] = React.useState<string | null>(null);
  React.useEffect(() => {
    const keys = URL_PARAMS;
    // For each key, if it's in the URL, set it in session storage
    for (const key of keys) {
      const param = searchParams.get(key);
      if (param) {
        sessionStorage.setItem(key, param);
      }
      if (key === AFFILIATE_PARAM) {
        if (param) {
          setAff(param);
        } else {
          // Also check session storage for affiliate and set state
          const sessionAff = sessionStorage.getItem(AFFILIATE_PARAM);
          if (sessionAff) {
            setAff(sessionAff);
          }
        }
      }
    }
  }, [searchParams]);
  const signupRoute = aff ? `${routes.SIGNUP}?aff=${aff}` : routes.SIGNUP;
  return (
    <Box>
      <HeroSection signupRoute={signupRoute} />
      <FeaturesSection />
      <TeamSection />
      <CustomerSupportSection />
    </Box>
  );
}
